import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetDealConfigQuery,
	useGetHubspotPipelinesQuery,
	useGetHubspotOwnersQuery,
	useSaveDealConfigMutation,
} from "@/pages/Private/redux/hubspot/hubspot.api";

import { DealConfig } from "../schema/hubspot";

/** Treat "undefined", "null", and empty strings as null */
const sanitizeStr = (val: string | null | undefined): string | null => {
	if (!val || val === "undefined" || val === "null") {
		return null;
	}

	return val;
};

export const HubspotDealConfigTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.hubspot.dealConfig.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: dealConfig, isLoading: isLoadingConfig } = useGetDealConfigQuery();
	const { data: pipelinesData } = useGetHubspotPipelinesQuery();
	const { data: ownersData } = useGetHubspotOwnersQuery();
	const [saveDealConfig, { isLoading: isSaving }] = useSaveDealConfigMutation();

	const [config, setConfig] = useState<DealConfig>({
		defaultPipeline: null,
		defaultDealStage: null,
		dealNameTemplate: "",
		closeDateOffset: 30,
		defaultContactOwner: null,
		defaultContactLifecycleStage: null,
		defaultContactLeadStatus: null,
		defaultCompanyOwner: null,
		defaultCompanyLifecycleStage: null,
		defaultCompanyLeadStatus: null,
	});

	useEffect(() => {
		if (dealConfig) {
			setConfig({
				defaultPipeline: sanitizeStr(dealConfig.defaultPipeline),
				defaultDealStage: sanitizeStr(dealConfig.defaultDealStage),
				dealNameTemplate:
					dealConfig.dealNameTemplate && dealConfig.dealNameTemplate !== "undefined"
						? dealConfig.dealNameTemplate
						: "",
				closeDateOffset: dealConfig.closeDateOffset ?? 30,
				defaultContactOwner: sanitizeStr(dealConfig.defaultContactOwner),
				defaultContactLifecycleStage: sanitizeStr(dealConfig.defaultContactLifecycleStage),
				defaultContactLeadStatus: sanitizeStr(dealConfig.defaultContactLeadStatus),
				defaultCompanyOwner: sanitizeStr(dealConfig.defaultCompanyOwner),
				defaultCompanyLifecycleStage: sanitizeStr(dealConfig.defaultCompanyLifecycleStage),
				defaultCompanyLeadStatus: sanitizeStr(dealConfig.defaultCompanyLeadStatus),
			});
		}
	}, [dealConfig]);

	const pipelineOptions: DropdownAutoCompleteItem[] = useMemo(
		() => pipelinesData?.pipelines?.map((p) => ({ id: p.id, title: p.label })) || [],
		[pipelinesData]
	);

	const stageOptions: DropdownAutoCompleteItem[] = useMemo(() => {
		if (!config.defaultPipeline) {
			return [];
		}

		const selectedPipeline = pipelinesData?.pipelines?.find((p) => p.id === config.defaultPipeline);

		return selectedPipeline?.stages?.map((s) => ({ id: s.id, title: s.label })) || [];
	}, [pipelinesData, config.defaultPipeline]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			ownersData?.owners?.map((o) => ({
				id: String(o.id),
				title: `${o.firstName || ""} ${o.lastName || ""} (${o.email})`.trim(),
			})) || [],
		[ownersData]
	);

	const handleSave = async () => {
		try {
			const payload: DealConfig = {
				defaultPipeline: sanitizeStr(config.defaultPipeline),
				defaultDealStage: sanitizeStr(config.defaultDealStage),
				dealNameTemplate:
					config.dealNameTemplate && config.dealNameTemplate !== "undefined"
						? config.dealNameTemplate
						: null,
				closeDateOffset: config.closeDateOffset ?? null,
				defaultContactOwner: sanitizeStr(config.defaultContactOwner),
				defaultContactLifecycleStage: sanitizeStr(config.defaultContactLifecycleStage),
				defaultContactLeadStatus: sanitizeStr(config.defaultContactLeadStatus),
				defaultCompanyOwner: sanitizeStr(config.defaultCompanyOwner),
				defaultCompanyLifecycleStage: sanitizeStr(config.defaultCompanyLifecycleStage),
				defaultCompanyLeadStatus: sanitizeStr(config.defaultCompanyLeadStatus),
			};

			await saveDealConfig(payload).unwrap();
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	};

	if (isLoadingConfig) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-gray-600 mb-1">{ts("description")}</div>

			<Grid container spacing={3}>
				<Grid size={{ md: 6, xs: 12 }}>
					<Dropdown
						data={pipelineOptions}
						defaultValue={
							config.defaultPipeline
								? pipelineOptions.find((p) => p.id === config.defaultPipeline)
								: undefined
						}
						floating={true}
						handleSelect={(value) => {
							setConfig((prev) => ({
								...prev,
								defaultPipeline: value.id as string,
								defaultDealStage: null,
							}));
						}}
						label={ts("defaultPipeline")}
					/>
				</Grid>

				<Grid size={{ md: 6, xs: 12 }}>
					<Dropdown
						key={config.defaultPipeline ?? "no-pipeline"}
						data={stageOptions}
						defaultValue={
							config.defaultDealStage
								? stageOptions.find((s) => s.id === config.defaultDealStage)
								: undefined
						}
						disabled={!config.defaultPipeline}
						floating={true}
						handleSelect={(value) => {
							setConfig((prev) => ({ ...prev, defaultDealStage: value.id as string }));
						}}
						label={ts("defaultStage")}
					/>
				</Grid>

				<Grid size={{ md: 6, xs: 12 }}>
					<InputField
						handleChange={(e) =>
							setConfig((prev) => ({ ...prev, dealNameTemplate: e.target.value }))
						}
						information={ts("dealNameTemplateHelp")}
						label={ts("dealNameTemplate")}
						name="dealNameTemplate"
						placeholder="{company} - {firstName} {lastName}"
						value={config.dealNameTemplate || ""}
					/>
				</Grid>

				<Grid size={{ md: 6, xs: 12 }}>
					<InputField
						handleChange={(e) =>
							setConfig((prev) => ({
								...prev,
								closeDateOffset: parseInt(e.target.value, 10) || 0,
							}))
						}
						information={ts("closeDateOffsetHelp")}
						label={ts("closeDateOffsetDays")}
						min={0}
						name="closeDateOffset"
						type="number"
						value={config.closeDateOffset ?? 30}
					/>
				</Grid>

				<Grid size={{ md: 6, xs: 12 }}>
					<Dropdown
						data={ownerOptions}
						defaultValue={
							config.defaultContactOwner
								? ownerOptions.find((o) => String(o.id) === String(config.defaultContactOwner))
								: undefined
						}
						floating={true}
						handleSelect={(value) => {
							setConfig((prev) => ({ ...prev, defaultContactOwner: value.id as string }));
						}}
						information={ts("defaultOwnerHelp")}
						label={ts("defaultOwner")}
					/>
				</Grid>
			</Grid>

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					title={t("basics.save")}
					type="button"
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
